import { EllipsisOutlined } from "@ant-design/icons";
import { Button, Card, Dropdown, Menu, Modal, Table } from "antd";
import axios from "axios";
import { message } from "components/Messages/Messages";
import MobileTable from "components/MobileTable/MobileTable";
import useViewport from "components/UseViewport/UseViewport";
import { useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { RegisteredStudentData } from "../AdminSingleEventPage";
import ManualRegistration from "./ManualRegistration";

interface Props {
  registeredStudentsData: RegisteredStudentData[];
  eventId: number;
  loading: boolean;
  onStudentDeregistered: (studentId: number) => void;
  onStudentRegistered: (studentId: number) => void;
}

const RegisteredStudents: React.FC<Props> = ({
  registeredStudentsData,
  loading,
  eventId,
  onStudentDeregistered,
  onStudentRegistered,
}) => {
  const { width } = useViewport();
  const history = useHistory();
  // TODO - fix width and padding
  // TODO - fix sorting
  // TODO - fix number logic
  const [loadingModal, setLoadingModal] = useState(false);
  const columns = [
    {
      title: "#",
      dataIndex: "number",
      key: "number",
    },
    {
      title: "Navn",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: RegisteredStudentData) => {
        return <Link to={`/admin/students/${record.id}`}>{name}</Link>;
      },
    },
    {
      title: "Brukernavn",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Linje",
      dataIndex: "study_programme_short_name",
      key: "study_programme",
    },
    {
      title: "Årstrinn",
      dataIndex: "year_level",
      key: "year_level",
    },
    {
      title: "Telefon",
      dataIndex: "mobile_number",
      key: "mobile_number",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Allergier",
      dataIndex: "allergies",
      key: "allergies",
    },
    {
      title: "Oppmøtt",
      dataIndex: "time_attended",
      key: "time_attended",
    },
    {
      title: "Påmeldt",
      dataIndex: "created_at",
      key: "created_at",
    },
    {
      key: "action",
      name: "action",
      dataIndex: "id",
      render: (studentId: number, record: RegisteredStudentData) => {
        const handleCreateTermViolation = () => {
          Modal.confirm({
            title: `Gi prikk`,
            content: `Er du sikker på at du vil gi ${record.name} en prikk?`,
            onOk: () => {
              setLoadingModal(true);
              return axios
                .post(`/events/${eventId}/create-term-violation/`, {
                  student: studentId,
                })
                .then(() => {
                  message("success", `Prikk gitt til ${record.name}`);
                })
                .catch(() => {
                  message("error", "En feil oppstod", `Prikk ble ikke gitt til ${record.name}`);
                })
                .finally(() => {
                  setLoadingModal(false);
                });
            },
            okButtonProps: {
              danger: true,
              loading: loadingModal,
            },
            okText: "Gi prikk",
            cancelText: "Avbryt",
          });
        };

        const handleDeregister = () => {
          Modal.confirm({
            title: `Meld av arrangement`,
            content: `Er du sikker på at du vil melde av ${record.name} fra arrangementet?`,
            onOk: () => {
              setLoadingModal(true);
              return axios
                .post(`/events/${eventId}/admin_unregister/`, {
                  student: studentId,
                })
                .then(() => {
                  message("success", `Studenten er meldt av arrangementet`);
                  onStudentDeregistered(studentId);
                })
                .catch(() => {
                  message("error", "En feil oppstod", "Studenten ble ikke meldt av arrangementet");
                })
                .finally(() => {
                  setLoadingModal(false);
                });
            },
            okButtonProps: {
              danger: true,
              loading: loadingModal,
            },
            okText: "Meld av",
            cancelText: "Avbryt",
          });
        };

        const menu = (
          <Menu>
            <Menu.Item key="1" onClick={handleCreateTermViolation} danger>
              Gi prikk
            </Menu.Item>
            <Menu.Item key="2" onClick={handleDeregister}>
              Meld av arrangement
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <Button icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const alreadyRegisteredStudentIds = useMemo(() => registeredStudentsData.map(student => student.id), [
    registeredStudentsData,
  ]);

  return (
    <Card title="Påmeldte studenter" style={{ width: "100%", marginTop: "16px" }}>
      {width > 768 ? (
        <Table
          style={{ width: "100%" }}
          dataSource={registeredStudentsData}
          columns={columns}
          pagination={false}
          loading={loading}
          locale={{ emptyText: loading ? "Laster inn påmeldte studenter..." : "Ingen påmeldte studenter" }}
        />
      ) : (
        <MobileTable
          data={registeredStudentsData}
          columns={[
            {
              title: "Navn",
              dataIndex: "name",
              key: "name",
            },
            {
              title: "Linje",
              dataIndex: "study_programme_name",
              key: "study_programme",
            },
            {
              title: "Årstrinn",
              dataIndex: "year_level",
              key: "year_level",
            },
            {
              title: "Telefon",
              dataIndex: "mobile_number",
              key: "mobile_number",
            },
            {
              title: "Email",
              dataIndex: "email",
              key: "email",
            },
            {
              title: "Allergier",
              dataIndex: "allergies",
              key: "allergies",
            },
            {
              title: "Påmeldt",
              dataIndex: "created_at",
              key: "created_at",
              render: date =>
                new Date(date as string).toLocaleString("nb-NO", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                }),
            },
            {
              title: "Oppmøtt",
              dataIndex: "time_attended",
              key: "time_attended",
              render: time =>
                time
                  ? new Date(time as string).toLocaleString("nb-NO", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "Ikke oppmøtt",
            },
          ]}
          loading={loading}
          onClick={id => history.push(`/admin/students/${id}/`)}
          emptyText="Ingen påmeldte studenter"
        />
      )}
      <ManualRegistration
        eventId={eventId}
        alreadyRegisteredStudentIds={alreadyRegisteredStudentIds}
        onRegistration={onStudentRegistered}
      />
    </Card>
  );
};

export default RegisteredStudents;
